
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SubscriptionPlanType } from "@/components/dashboard/subscription/utils";

interface CheckoutSessionResult {
  success: boolean;
  url?: string;
  error?: string;
  isDowngrade?: boolean;
  message?: string;
  redirectUrl?: string;
  isProratedUpgrade?: boolean;
}

export const useCheckoutSession = () => {
  const [isLoading, setIsLoading] = useState(false);

  const createCheckoutSession = async (
    userId: string,
    planName: SubscriptionPlanType,
    userEmail?: string,
    currentPlan?: string,
    isAnnual: boolean = false
  ): Promise<CheckoutSessionResult> => {
    setIsLoading(true);
    console.log('🔄 Starting checkout session creation:', { 
      userId, 
      planName, 
      currentPlan,
      isAnnual 
    });
    
    try {
      // Determine if this is an upgrade/downgrade or a new subscription
      const isUpgrade = !!currentPlan && currentPlan !== 'trial';
      console.log(`🔄 Subscription type: ${isUpgrade ? 'Upgrade/Change' : 'New subscription'}`);
      
      // Create the checkout session using Supabase Edge Function
      console.log('🔄 Invoking create-checkout-session edge function');
      const { data, error } = await supabase.functions.invoke(
        'create-checkout-session',
        {
          method: 'POST',
          body: { 
            userId, 
            userEmail, 
            planName,
            currentPlan,
            isUpgrade,
            isAnnual
          },
        }
      );

      if (error) {
        console.error('❌ Edge function error:', error);
        toast.error('Failed to create checkout session: ' + error.message);
        return { success: false, error: error.message };
      }
      
      console.log('✅ Edge function response:', data);
      
      // Handle downgrade case
      if (data?.isDowngrade) {
        console.log('⚠️ Downgrade detected - redirecting to billing portal');
        toast.info(data.message || 'Please use the billing portal to downgrade your plan');
        return { 
          success: false, 
          isDowngrade: true, 
          message: data.message || 'Please use the billing portal to downgrade your plan' 
        };
      }
      
      // Handle prorated upgrade case (subscription was updated directly)
      if (data?.message && data?.redirectUrl) {
        console.log('✅ Prorated upgrade successful:', data.message);
        toast.success(data.message || 'Your subscription has been upgraded successfully!');
        
        // For prorated upgrades, we'll redirect the user to the dashboard
        // with a success parameter
        window.location.href = data.redirectUrl;
        
        return { 
          success: true, 
          message: data.message,
          isProratedUpgrade: true 
        };
      }
      
      if (data?.url) {
        console.log('✅ Checkout session created successfully, redirecting to:', data.url);
        // Return success with the URL but don't redirect yet
        return { success: true, url: data.url };
      } else if (data?.error) {
        console.error('❌ Checkout error:', data.error);
        toast.error(data.error);
        return { success: false, error: data.error };
      } else {
        const errorMessage = 'Invalid response from server';
        console.error('❌', errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to create checkout session';
      console.error('❌ Exception during checkout:', error);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return { createCheckoutSession, isLoading };
};
